import {useCallback, useEffect, useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {createPersister} from '@github-ui/react-query-persister'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {PullsInboxSection} from '@github-ui/shared-dashboard-lists/pulls/PullsInboxSection'
import type {DashboardSectionConfig} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  usePullsDashboardFetchedQuery,
  usePullsDashboardLoadingStateActions,
  usePullsDashboardMaxPrAge,
  usePullsDashboardPayload,
  usePullsDashboardSectionRowCount,
} from '../contexts/PullsDashboardSurfaceStateContext'
import {useSaveCollapsedSection} from '../hooks/use-save-collapsed-section'
import {useSectionData} from '../hooks/use-section-data'
import {useSectionEmptyMemory} from '../hooks/use-section-empty-memory'
import {useVisibleInboxSections} from '../hooks/use-visible-inbox-sections'
import {SectionDataResultSchema} from '../types/routes-types'
import {getActiveInboxQueryName, pullsDashboardInboxRoute} from './pulls-dashboard-inbox-route'

export function PullsDashboardInboxComponent() {
  const {visibleSections, firstVisibleSectionId} = useVisibleInboxSections()

  return (
    <div data-testid="pulls-dashboard-inbox" className="tmp-pt-3">
      <div className="d-flex flex-column gap-4">
        {visibleSections.map(section => (
          <ErrorBoundary
            key={section.id}
            boundaryName={`InboxDashboardSection-${section.id}`}
            fallback={
              <section aria-label={section.name}>
                <h2 className="h4 mb-2">{section.name}</h2>
                <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
              </section>
            }
          >
            <InboxDashboardSection section={section} firstVisibleSectionId={firstVisibleSectionId} />
          </ErrorBoundary>
        ))}
      </div>
    </div>
  )
}

const sectionDataResultValidator = Compile(SectionDataResultSchema)

function InboxDashboardSection({
  section,
  firstVisibleSectionId,
}: {
  section: DashboardSectionConfig
  firstVisibleSectionId: string
}) {
  const queryName = getActiveInboxQueryName(section.id, firstVisibleSectionId)
  if (!queryName) return null
  return <InboxDashboardSectionContent section={section} queryName={queryName} />
}

function InboxDashboardSectionContent({
  section,
  queryName,
}: {
  section: DashboardSectionConfig
  queryName: NonNullable<ReturnType<typeof getActiveInboxQueryName>>
}) {
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: sectionDataResultValidator,
        sendAnalyticsEvent,
        prefix: 'pulls-dashboard-inbox',
      }),
    [sendAnalyticsEvent],
  )

  const {data, isPending, isError} = useRouteQuery(pullsDashboardInboxRoute, queryName, {persister})

  const {inboxSectionQueries: inboxSectionQueriesFromLayout, collapsedSections: collapsedSectionsFromLayout} =
    usePullsDashboardPayload()
  const fetchedQuery = usePullsDashboardFetchedQuery()
  const maxPrAge = usePullsDashboardMaxPrAge()

  const {isCollapsed, setCollapsed} = useSaveCollapsedSection(section.id, collapsedSectionsFromLayout ?? [])
  const {knownEmpty, recordEmptiness} = useSectionEmptyMemory(section.id)
  const skeletonItemCount = usePullsDashboardSectionRowCount(section.id)
  const {recordSectionRowCount, recordSectionCollapsedState} = usePullsDashboardLoadingStateActions()

  // Remember whether the section was empty so a subsequent load can skip the
  // skeleton flash for a section we already know has no results.
  useEffect(() => {
    if (isPending || isError || !data) return
    recordEmptiness((data.totalCount ?? 0) === 0)
  }, [data, isError, isPending, recordEmptiness])

  const queryId = inboxSectionQueriesFromLayout?.[section.id] ?? section.id

  // Visible count is reset on changes to query or max PR age
  const scopeKey = [queryId, maxPrAge, fetchedQuery].join(' ')

  const sectionData = useSectionData({
    sectionId: section.id,
    scopeKey,
    primaryData: data,
    initialVisibleCount: section.maxItems,
    isCollapsed,
  })

  useEffect(() => {
    if (isCollapsed || isPending || isError) return
    recordSectionRowCount(section.id, sectionData.enrichedPulls.length)
  }, [isCollapsed, isError, isPending, recordSectionRowCount, section.id, sectionData.enrichedPulls.length])

  const onCollapsedChange = useCallback(
    (collapsed: boolean) => {
      setCollapsed(collapsed)
      recordSectionCollapsedState(section.id, collapsed)
    },
    [recordSectionCollapsedState, section.id, setCollapsed],
  )

  return (
    <PullsInboxSection
      config={section}
      {...sectionData}
      isPending={isPending}
      isError={isError}
      totalCount={data?.totalCount ?? 0}
      trackHPC={section.id === 'review-requested'}
      isCollapsed={isCollapsed}
      knownEmpty={knownEmpty}
      skeletonItemCount={skeletonItemCount}
      onCollapsedChange={onCollapsedChange}
      isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
    />
  )
}
